import config from "src/config";

// 라벨 출력 API
const labelUrl = `${config.apiUrl}/label`;

/** REACT_APP_MODE=dev 일 때 라벨 출력 생략. 훅(useQzPrint 등)에서도 동일 조건으로 사용 */
export const shouldSkipLabelPrintInDev = (): boolean => {
	if (process.env.REACT_APP_MODE !== 'dev') return false;
	console.warn('[MES] 개발 모드(REACT_APP_MODE=dev): 라벨 출력을 건너뜁니다.');
	return true;
};

/**
 * 라벨 데이터 DTO
 */
export interface LabelDataDto {
	lotNo: string;
	itemCd: string;
	itemSpec: string;
	inboundDate: string;
    warehouseNm: string;
	customer: string;
	customerItemCd: string;
}

/**
 * 단일 LOT 번호로 라벨 출력 페이지 열기
 * 새 창에서 라벨 출력 페이지를 열어 브라우저 인쇄 기능 사용
 * 
 * @param lotNo - 출력할 LOT 번호
 * @returns 새 창 객체
 */
export const printLabel = (lotNo: string): Window | null => {
	if (shouldSkipLabelPrintInDev()) return null;

	const url = `${labelUrl}/print/${lotNo}`;
	
	// 새 창에서 라벨 출력 페이지 열기
	const printWindow = window.open(url, '_blank', 'width=800,height=600');
	
	return printWindow;
};

/**
 * 여러 LOT 번호로 라벨 일괄 출력
 * 각 LOT에 대해 순차적으로 출력 페이지를 열고 인쇄
 * 
 * @param lotNos - 출력할 LOT 번호 배열
 * @param interval - 각 출력 사이의 간격(ms), 기본값 1000ms
 */
export const printBulkLabels = (lotNos: string[], interval: number = 1000): void => {
	if (shouldSkipLabelPrintInDev()) return;

	lotNos.forEach((lotNo, index) => {
		setTimeout(() => {
			printLabel(lotNo);
		}, index * interval);
	});
};

/**
 * 라벨 출력 URL 가져오기
 * iframe이나 다른 방식으로 라벨을 표시할 때 사용
 * 
 * @param lotNo - LOT 번호
 * @returns 라벨 출력 페이지 URL
 */
export const getLabelPrintUrl = (lotNo: string): string => {
	return `${labelUrl}/print/${lotNo}`;
};

// ========================================
// POST 방식 라벨 출력 (DB 조회 없이 데이터 직접 전달)
// ========================================

/**
 * 라벨 출력용 데이터 인터페이스
 * POST 방식으로 프론트엔드에서 직접 데이터 전달
 */
export interface LabelPrintData {
	lotNo: string;
	itemCd: string;
	itemNm: string;           // 품명 (라벨 품명란 표시용)
	itemSpec: string;         // 품목 규격
	transportDate: string;   // (구) inboundDate
	itemAccountNm: string;   // 라벨 표시용 (키오스크: 품목계정 코드명, MES: 입출고구분 등)
	storageLocation: string; // 제품·재공품이면 "-"
	qty: string;             // "2,000EA" 형태
	customer: string;
	customerItemCd: string;
	labelType: string;       // "[자재입고]" | "[생산입고]"
}

/**
 * POST 방식으로 라벨 출력 페이지 열기 (Promise 기반)
 * DB 조회 없이 프론트엔드에서 전달한 데이터로 라벨 생성
 * 각 라벨마다 고유한 창에서 열림
 * 
 * @param data - 라벨 출력 데이터
 * @returns Promise<Window | null>
 */
export const printLabelWithData = (data: LabelPrintData): Promise<Window | null> => {
	if (shouldSkipLabelPrintInDev()) return Promise.resolve(null);

	return new Promise((resolve, reject) => {
		const url = `${labelUrl}/print`;
		
		// 고유한 타겟명 생성 (타임스탬프 + 랜덤)
		const uniqueId = `label_${data.lotNo}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
		
		// console.log(`🔷 창 열기 시작: ${uniqueId}`);
		
		// Form 생성 (POST 방식)
		const form = document.createElement('form');
		form.method = 'POST';
		form.action = url;
		form.target = uniqueId;
		form.style.display = 'none';
		
		// 각 필드를 hidden input으로 추가
		Object.entries(data).forEach(([key, value]) => {
			const input = document.createElement('input');
			input.type = 'hidden';
			input.name = key;
			input.value = String(value || '');
			form.appendChild(input);
		});
		
		document.body.appendChild(form);
		
		// 새 창 열기 (고유한 이름으로)
		const printWindow = window.open('', uniqueId, 'width=800,height=600');
		
		if (!printWindow) {
			console.error(`  창 열기 실패: ${uniqueId} (팝업 차단 확인)`);
			document.body.removeChild(form);
			reject(new Error('팝업 차단으로 인해 창을 열 수 없습니다'));
			return;
		}
		
		console.log(`  창 열림: ${uniqueId}`);
		
		// 충분한 딜레이 후 submit
		setTimeout(() => {
			// console.log(`    데이터 전송: ${data.lotNo}`);
			form.submit();
			
			// form 제거
			setTimeout(() => {
				if (document.body.contains(form)) {
					document.body.removeChild(form);
				}
				resolve(printWindow);
			}, 1000);
		}, 200);
	});
};

/**
 * POST 방식으로 여러 라벨 순차 출력 (기존 방식)
 * @deprecated printBulkLabelsAtOnce 사용 권장
 * @param dataList - 라벨 출력 데이터 배열
 * @param interval - 각 출력 사이의 간격(ms), 기본값 1000ms
 */
export const printBulkLabelsWithData = (
	dataList: LabelPrintData[], 
	interval: number = 1000
): void => {
	if (shouldSkipLabelPrintInDev()) return;

	console.log(`========== ${dataList.length}개 라벨 출력 시작 ==========`);
	
	dataList.forEach((data, index) => {
		setTimeout(() => {
			console.log(`[${index + 1}/${dataList.length}] 출력 시작: ${data.lotNo}`);
			printLabelWithData(data);
		}, index * interval);
	});
};

/**
 * POST 방식으로 여러 라벨 완전 순차 출력 (기존 방식)
 *
 * @deprecated printBulkLabelsAtOnce 사용 권장 (한 번의 인쇄 다이얼로그)
 * @param dataList - 라벨 출력 데이터 배열
 * @param interval - 각 출력 사이의 간격(ms), 기본값 2000ms (2초)
 */
export const printBulkLabelsWithDataSequential = async (
	dataList: LabelPrintData[],
	interval: number = 2000
): Promise<void> => {
	if (shouldSkipLabelPrintInDev()) return;

	console.log(`========== ${dataList.length}개 라벨 출력 시작 (순차) ==========`);

	for (let i = 0; i < dataList.length; i++) {
		const data = dataList[i];
		console.log(`\n[${i + 1}/${dataList.length}] 출력 시작: ${data.lotNo}`);

		try {
			await printLabelWithData(data);
			console.log(`[${i + 1}/${dataList.length}]   출력 완료: ${data.lotNo}`);
		} catch (error) {
			console.error(`[${i + 1}/${dataList.length}]   출력 실패: ${data.lotNo}`, error);
		}

		if (i < dataList.length - 1) {
			await new Promise(resolve => setTimeout(resolve, interval));
		}
	}

	console.log(`\n========== ${dataList.length}개 라벨 출력 완료 ==========`);
};

// ========================================
// 신규: 다중 라벨 일괄 출력 (권장)
// ========================================

/**
 * 다중 라벨 요청 DTO
 */
export interface LabelBulkRequest {
	labels: LabelPrintData[];
}

/**
 * 다중 라벨 일괄 출력 (한 번의 인쇄로 모든 라벨 출력)
 *
 * 기존 순차 출력과의 차이:
 * - 기존: N개 라벨 = N번의 인쇄 다이얼로그
 * - 신규: N개 라벨 = 1번의 인쇄 다이얼로그
 *
 * @param dataList - 라벨 출력 데이터 배열
 * @returns Promise<Window | null> - 출력 창 객체
 */
export const printBulkLabelsAtOnce = async (
	dataList: LabelPrintData[],
): Promise<Window | null> => {
	if (shouldSkipLabelPrintInDev()) return null;

	console.log(`========== ${dataList.length}개 라벨 일괄 출력 시작 ==========`);

	if (!dataList || dataList.length === 0) {
		console.warn('출력할 라벨이 없습니다.');
		return null;
	}

	const url = `${labelUrl}/print/browser`;

	try {
		const requestBody: LabelBulkRequest = { labels: dataList };
		const response = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(requestBody),
		});

		if (!response.ok) {
			const errorText = await response.text();
			console.error('  API 응답 오류:', response.status, errorText);
			throw new Error(`라벨 생성 실패: ${response.status}`);
		}

		const html = await response.text();
		const printWindow = window.open('', '_blank', 'width=800,height=600');

		if (!printWindow) {
			console.error('  팝업 차단으로 인해 창을 열 수 없습니다.');
			throw new Error('팝업 차단으로 인해 창을 열 수 없습니다. 팝업 차단을 해제해주세요.');
		}

		printWindow.document.open();
		printWindow.document.write(html);
		printWindow.document.close();

		// 렌더 완료 후 인쇄 대화상자 자동 오픈 (브라우저 대체 시 프린터로 출력 가능하도록)
		setTimeout(() => {
			try {
				if (!printWindow.closed) printWindow.print();
			} catch {
				// 창이 이미 닫혀 있으면 무시
			}
		}, 400);

		console.log(`========== ${dataList.length}개 라벨 일괄 출력 완료 ==========`);
		return printWindow;
	} catch (error) {
		console.error('다중 라벨 출력 실패:', error);
		throw error;
	}
};
